import { useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { Minus, Plus, Trash2, Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { API, PRICE, PRODUCT_NAME, colourById } from "@/lib/brand";

const Line = ({ item }) => {
  const { setQty } = useCart();
  const c = colourById(item.colour);
  const id = `${item.colour}-${item.size}`;
  return (
    <motion.li layout initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 30 }} data-testid={`cart-item-${id}`} className="flex gap-4 py-5 border-b border-white/10">
      <img src={c.product} alt={c.label} className="h-24 w-20 rounded-xl object-cover bg-black" />
      <div className="flex-1">
        <p className="font-display font-bold">{PRODUCT_NAME}</p>
        <p className="font-mono text-xs uppercase tracking-widest text-slate-300 mt-1">{c.label} · {item.size}</p>
        <div className="mt-3 flex items-center gap-3">
          <button data-testid={`cart-item-decrease-${id}`} onClick={() => setQty(item.colour, item.size, item.quantity - 1)} className="h-8 w-8 rounded-full border border-white/15 grid place-items-center hover:border-white"><Minus className="h-3 w-3" /></button>
          <span data-testid={`cart-item-qty-${id}`} className="font-mono w-5 text-center">{item.quantity}</span>
          <button data-testid={`cart-item-increase-${id}`} onClick={() => setQty(item.colour, item.size, item.quantity + 1)} className="h-8 w-8 rounded-full border border-white/15 grid place-items-center hover:border-white"><Plus className="h-3 w-3" /></button>
          <button data-testid={`cart-item-remove-${id}`} onClick={() => setQty(item.colour, item.size, 0)} className="ml-auto text-slate-400 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
      <p className="font-display font-bold">£{PRICE * item.quantity}</p>
    </motion.li>
  );
};

export const CartDrawer = () => {
  const { items, open, setOpen, total, count } = useCart();
  const [busy, setBusy] = useState(false);

  const checkout = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, { items, origin_url: window.location.origin });
      if (!data?.checkout_url) throw new Error("no url");
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Checkout is unavailable right now. Please try again.");
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent data-lenis-prevent data-testid="cart-drawer" className="w-full sm:max-w-md bg-[#0B0F15] border-l border-white/10 text-white flex flex-col">
        <SheetHeader>
          <SheetTitle className="font-display font-black uppercase text-2xl text-white">Your bag ({count})</SheetTitle>
          <SheetDescription className="text-slate-300">Every hoodie £{PRICE}, every size.</SheetDescription>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto -mx-6 px-6">
          {items.length === 0 ? (
            <p data-testid="cart-empty" className="py-16 text-center text-slate-300">Your bag is empty. Find your colour.</p>
          ) : (
            <ul><AnimatePresence initial={false}>{items.map((i) => <Line key={`${i.colour}-${i.size}`} item={i} />)}</AnimatePresence></ul>
          )}
        </div>
        <div className="border-t border-white/10 pt-5">
          <div className="flex justify-between font-display text-lg"><span>Subtotal</span><span data-testid="cart-subtotal" className="font-extrabold">£{total.toFixed(2)}</span></div>
          <p className="mt-1 text-xs text-slate-400">Delivery address and any applicable tax confirmed at checkout.</p>
          <button data-testid="checkout-button" disabled={!items.length || busy} onClick={checkout} className="glow-btn mt-5 w-full justify-center h-14 disabled:opacity-50 disabled:pointer-events-none">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} Secure checkout
          </button>
          <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-widest text-slate-400">Powered by Stripe · Card · Apple Pay · Google Pay</p>
        </div>
      </SheetContent>
    </Sheet>
  );
};
